/**
 * PromotionBanner Component
 *
 * A promotional banner component for highlighting subscription offers or special promotions.
 * Features a customizable title, description, call-to-action button, and visual elements.
 */
import Image, { type StaticImageData } from 'next/image';

import bannerImage from '@ui/assets/images/become_a_pro.png';
import { DecorativeCircles } from '@ui/components/features/landing/decorativeCircles';
import { Card, CardTitle } from '@ui/components/ui/card';
import { cn } from '@ui/lib/utils';

import { Button } from '../../../common/button';

export interface PromotionBannerProps {
  /**
   * Banner title text
   * @default "Unlock Your Exclusive 30-Day Trial!"
   */
  title?: string;

  /**
   * Banner description text
   * @default "Subscribe now and explore all features risk-free."
   */
  description?: string;

  /**
   * Button label text
   * @default "Become a Mentor"
   */
  buttonLabel?: string;

  /**
   * Optional click handler for the button
   */
  onButtonClick?: () => void;

  /**
   * Optional image URL or StaticImageData to override the default banner image
   * If not provided, the default become_a_pro.png image from assets will be used
   */
  imageUrl?: string | StaticImageData;

  /**
   * Additional props to be spread to the container div element
   */
  className?: string;
  style?: React.CSSProperties;
  // Allow other HTML div element props
  [key: string]: string | number | boolean | React.ReactNode | (() => void) | React.CSSProperties | undefined;
}

/**
 * PromotionBanner component for displaying promotional content
 *
 * @example
 * ```tsx
 * <PromotionBanner
 *   title="Special Offer!"
 *   description="Limited time promotion for new users."
 *   buttonLabel="Sign Up Now"
 *   onButtonClick={() => console.log('Button clicked')}
 * />
 * ```
 */
export const PromotionBanner = ({
  title = 'Unlock Your Exclusive 30-Day Trial!',
  description = 'Subscribe now and explore all features risk-free.',
  buttonLabel = 'Become a Mentor',
  onButtonClick,
  imageUrl,
  className,
  ...props
}: PromotionBannerProps) => {
  return (
    <Card
      className={cn(
        'w-full md:w-auto bg-primary-0 md:bg-red-50 relative overflow-hidden rounded-none md:rounded-lg border-none shadow-none',
        className
      )}
      {...props}
    >
      <div className="flex flex-col md:flex-row justify-start items-center py-0 md:py-10 px-0 md:px-10 xl:px-20 gap-0 md:gap-6 xl:gap-12">
        {/* Image container with responsive sizing */}
        <div className="relative w-full md:w-[60%] h-40 md:h-60">
          {/* Background div */}
          <div className="w-full md:w-[calc(100%-20px)] h-32 md:h-55 bg-primary-200 absolute bottom-0 left-0 rounded-none md:rounded-lg" />
          {/* Image section */}
          <div className="absolute w-[calc(100%-16px)] md:w-[calc(100%-20px)] h-38 md:h-55 right-2 md:right-0 top-0">
            <Image
              className="w-full h-full border-none border-2 border-neutral-100 rounded-none md:rounded-lg"
              src={imageUrl || bannerImage}
              alt="Promotion banner"
              fill
              sizes="(max-width: 768px) 100vw, 45vw"
              quality={60}
              style={{ objectFit: 'cover' }}
              priority={true}
            />
          </div>
        </div>

        {/* Content section */}
        <div className="flex-1 self-stretch flex flex-col bg-red-50 justify-center items-start rounded-b-lg p-5 pt-4 gap-4">
          <div className="self-stretch flex flex-col justify-start items-start ">
            <CardTitle className="self-stretch text-neutral-800 text-lg font-semibold font-manrope">{title}</CardTitle>
            <p className="self-stretch text-neutral-700 text-sm font-normal font-manrope">{description}</p>
          </div>

          {/* Button */}
          <Button label={buttonLabel} variant="primary" onClick={onButtonClick} className="" />
        </div>
        <DecorativeCircles className="absolute w-[480px] -right-50 -top-10 pointer-events-none hidden md:block" />
      </div>
    </Card>
  );
};
